import React from "react";
import { NavLink } from "react-router-dom";
import { LayoutDashboard, Map, Calculator, ScanLine, GraduationCap, Tag, Shield, Building2, LogOut, Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import PoweredBy from "@/components/PoweredBy";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/roadmap", label: "Action Roadmap", icon: Map, testid: "nav-roadmap" },
  { to: "/pnl-decoder", label: "P&L Decoder", icon: ScanLine, testid: "nav-pnl" },
  { to: "/calculator", label: "Profit Calculator", icon: Calculator, testid: "nav-calculator" },
  { to: "/lessons", label: "Lessons", icon: GraduationCap, testid: "nav-lessons" },
  { to: "/pricing", label: "Pricing", icon: Tag, testid: "nav-pricing" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = React.useState(false);

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm transition-all duration-200 ${
      isActive ? "tenant-light-bg tenant-text font-semibold" : "text-neutral-600 hover:bg-neutral-100"
    }`;

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Mobile top bar */}
      <div className="flex items-center justify-between border-b border-[#E5E2DC] bg-white px-4 py-3 lg:hidden">
        <Logo />
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="p-2">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <div className="mx-auto flex max-w-[1400px]">
        {/* Sidebar */}
        <aside className={`${open ? "block" : "hidden"} fixed inset-x-0 top-[57px] z-30 border-b border-[#E5E2DC] bg-white p-4 lg:static lg:block lg:w-64 lg:shrink-0 lg:border-b-0 lg:border-r lg:min-h-screen lg:p-6`}>
          <div className="mb-8 hidden lg:block"><Logo /><PoweredBy className="mt-1.5 pl-0.5" /></div>
          <nav className="space-y-1.5">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={n.testid} className={linkClass} onClick={() => setOpen(false)}>
                <n.icon size={18} strokeWidth={1.75} />
                {n.label}
              </NavLink>
            ))}
            {(user?.role === "admin" || user?.role === "partner_admin") && (
              <NavLink to="/admin" data-testid="nav-admin" className={linkClass} onClick={() => setOpen(false)}>
                <Shield size={18} strokeWidth={1.75} />
                {user?.role === "admin" ? "Members" : "My Members"}
              </NavLink>
            )}
            {user?.role === "admin" && (
              <NavLink to="/admin/partners" data-testid="nav-partners" className={linkClass} onClick={() => setOpen(false)}>
                <Building2 size={18} strokeWidth={1.75} />
                Partners
              </NavLink>
            )}
          </nav>
          <div className="mt-8 border-t border-[#E5E2DC] pt-4">
            <div className="mb-3 px-3.5">
              <p className="text-sm font-semibold text-neutral-800" data-testid="sidebar-user-name">{user?.name}</p>
              <p className="text-xs text-neutral-500">{user?.business_name || user?.email}</p>
            </div>
            <button
              data-testid="logout-btn"
              onClick={() => logout()}
              className="flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm text-neutral-600 transition-colors hover:bg-neutral-100"
            >
              <LogOut size={18} strokeWidth={1.75} /> Sign out
            </button>
          </div>
        </aside>

        <main className="min-w-0 flex-1 px-4 py-8 sm:px-6 lg:px-10 lg:py-12">{children}</main>
      </div>
    </div>
  );
}
